import { defineComponent as _defineComponent } from 'vue'
import { queries } from "@/apollo";
import {
  User,
  StatementType,
  ExternalCompany,
PositionType,
TtCompanyReport,
} from "@/CodegenTypes";
import type { ModernTtUserReport } from "@/types/reportTypes";
import { first, sumBy, some } from "lodash";
import { formatDollar } from "@/helpers/formatDollar";

import { FEATURE_TOGGLES } from "@/config";
import CompanyPlTable from "@/components/modules/report/CompanyPlTable.vue";
import { computed, ref } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useRoute } from "vue-router/composables";
import TtPositionStatementsModal from "@/components/modules/report/TtPositionStatementsModal.vue";
import TtTicketsModal from "@/components/modules/report/TtTicketsModal.vue";
import TtStrikeStatementsModal from "@/components/modules/report/TtStrikeStatementsModal.vue";
import TtOutsetStrikeStatementsModal from "@/components/modules/report/TtOutsetStrikeStatementsModal.vue";
import { canSeeTtPopl } from "@/helpers/user/canSeeTtPopl";
import { useTranslation } from "@/store/translation";


export default /*#__PURE__*/_defineComponent({
  __name: 'CompanyFullReport',
  setup(__props) {

const route = useRoute();
const { $t } = useTranslation();


const selectedStartReleaseDate = computed(() => {
  const query = route.query.startReleaseDate;
  return Array.isArray(query) ? first(query) ?? undefined : query;
});

const selectedEndReleaseDate = computed(() => {
  const query = route.query.endReleaseDate;
  return Array.isArray(query) ? first(query) ?? undefined : query;
});

const routeUserId = computed(() => {
  const query = route.query.userId;
  return Array.isArray(query) ? first(query) ?? undefined : query;
});

const selectedDateRangeReady = computed(() =>
  !!selectedStartReleaseDate.value && !!selectedEndReleaseDate.value
);

const fullReportVariables = computed(() => ({
  releaseDateStart: selectedStartReleaseDate.value,
  releaseDateEnd: selectedEndReleaseDate.value,
  userId: routeUserId.value,
}));

const companyReportVariables = computed(() => ({
  releaseDateStart: selectedStartReleaseDate.value,
  releaseDateEnd: selectedEndReleaseDate.value,
}));

const loading = computed(() => {
  return (
    meLoading.value || fullReportLoading.value || companyReportLoading.value
  );
});

const { result: meResult, loading: meLoading } = useQuery<{ me: User }>(
  queries.me,
  null,
  {
    fetchPolicy: "cache-first",
  }
);

const me = computed(() => meResult.value?.me);

const { result: fullReportResult, loading: fullReportLoading } = useQuery<{
  ttFullReport: ModernTtUserReport;
}>(queries.ttFullReport, fullReportVariables, {
  enabled: computed(() => selectedDateRangeReady.value && !!routeUserId.value),
});

const fullReport = computed(() => fullReportResult.value?.ttFullReport);

const { result: companyReportResult, loading: companyReportLoading } =
  useQuery<{
    ttCompanyReport: TtCompanyReport;
  }>(queries.ttCompanyReport, companyReportVariables, {
    enabled: selectedDateRangeReady,
  });

const showStrikeModal = ref(false);
const showTicketsModal = ref(false);
const showOutsetStrikeModal = ref(false);
const showPositionModal = ref(false);
const selectedUserId = ref<null | User["userId"]>(null);
const includeDownlines = ref(true);
const selectedExternalCompanyId = ref<null | ExternalCompany["userId"]>(null);
const selectedStatementType = ref<StatementType>(StatementType.TicketIn);

const isNotAdminViewingAdmin = computed(() => {
  return route.query?.userId === "admin" && me.value?.userId !== "admin";
});

const seePoPl = computed(() => {
  return canSeeTtPopl(me.value);
});

const downlinesWithTickets = computed(() => {
  return fullReport.value?.downlines
    ?.filter((dl) =>
      some(
        Object.values(dl).filter((val) => typeof val === "number" && val !== 0)
      )
    )
    .map((dl) => {
      const shareClaimPositionStatements = dl.positionStatements?.filter(
        (s) => s.positionType === PositionType.Outset
      );

      return {
        ...dl,
        shareClaim: shareClaimPositionStatements
          ? sumBy(shareClaimPositionStatements, (s) => s.uplinePosition)
          : 0,
        total: shareClaimPositionStatements
          ? sumBy(shareClaimPositionStatements, (s) => s.uplinePosition) +
            dl.netBal
          : dl.netBal,
      };
    });
});
const downlinesSummary = computed(() => {
  return {
    amount: sumBy(downlinesWithTickets.value, (dl) => dl.amount),
    rebate: sumBy(downlinesWithTickets.value, (dl) => dl.rebate),
    amountLessRebate: sumBy(
      downlinesWithTickets.value,
      (dl) => dl.amountLessRebate
    ),
    fixedExpenses: sumBy(downlinesWithTickets.value, (dl) => dl.fixedExpenses),
    strikeAmount: sumBy(downlinesWithTickets.value, (dl) => dl.strikeAmount),
    net: sumBy(downlinesWithTickets.value, (dl) => dl.net),
    companyStake: sumBy(downlinesWithTickets.value, (dl) => dl.companyStake),
    position: sumBy(downlinesWithTickets.value, (dl) => dl.position),
    netBal: sumBy(downlinesWithTickets.value, (dl) => dl.netBal),
    shareClaim: sumBy(downlinesWithTickets.value, (dl) => dl.shareClaim),
    total: sumBy(downlinesWithTickets.value, (dl) => dl.total),
  };
});

const selectedUserStatement = computed(() => {
  if (!fullReport.value) {
    return null;
  }
  return [
    fullReport.value.consolidated,
    ...(fullReport.value.downlines ?? []),
  ].find((s) => s.userId === selectedUserId.value);
});



function showPosition(userId?: User["userId"]) {
  if (userId) {
    selectedUserId.value = userId;
  }
  showPositionModal.value = true;
}
function showStrike(
  userId: User["userId"] | undefined,
  statementType: StatementType
) {
  if (userId) {
    selectedUserId.value = userId;
  }
  selectedStatementType.value = statementType;
  showStrikeModal.value = true;
}
function showTickets(
  userId: User["userId"] | undefined,
  includeDownlinesVar: boolean
) {
  if (userId) {
    selectedUserId.value = userId;
  }
  includeDownlines.value = includeDownlinesVar;
  showTicketsModal.value = true;
}
function showOutsetStrike(userId: ExternalCompany["userId"]) {
  selectedExternalCompanyId.value = userId;
  showOutsetStrikeModal.value = true;
}

return { __sfc: true,route, $t, selectedStartReleaseDate, selectedEndReleaseDate, routeUserId, selectedDateRangeReady, fullReportVariables, companyReportVariables, loading, meResult, meLoading, me, fullReportResult, fullReportLoading, fullReport, companyReportResult, companyReportLoading, showStrikeModal, showTicketsModal, showOutsetStrikeModal, showPositionModal, selectedUserId, includeDownlines, selectedExternalCompanyId, selectedStatementType, isNotAdminViewingAdmin, seePoPl, downlinesWithTickets, downlinesSummary, selectedUserStatement, showPosition, showStrike, showTickets, showOutsetStrike, StatementType, formatDollar, FEATURE_TOGGLES, CompanyPlTable, TtPositionStatementsModal, TtTicketsModal, TtStrikeStatementsModal, TtOutsetStrikeStatementsModal }
}

})